// Route file: routing + metadata + page composition only.
// All UI lives in components.
import { Sparkle } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CompareTable } from "@/components/marketing/compare-table";
import { FAQ } from "@/components/marketing/faq";
import { HeroDropzone } from "@/components/marketing/hero-dropzone";
import { HowItWorks } from "@/components/marketing/how-it-works";
import { TrustSignal } from "@/components/marketing/trust-signal";
import { WhySection } from "@/components/marketing/why-section";
import { ActionCard, ActionRowItem } from "@/components/tools/action-card";
import { actionsByCategory, CATEGORY_LABELS, upcomingActions } from "@/lib/tools/catalog";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "pdfisfun — work with any PDF in your browser" },
      {
        name: "description",
        content:
          "Merge, sign, compress, split, watermark, and convert PDFs. No account. No upload to us.",
      },
    ],
  }),
  component: HomeRoute,
});

function HomeRoute() {
  const groups = actionsByCategory();
  const [heroExpanded, setHeroExpanded] = useState(false);

  useEffect(() => {
    const schedule =
      "requestIdleCallback" in window
        ? requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 200);
    const cancel = "cancelIdleCallback" in window ? cancelIdleCallback : clearTimeout;
    const id = schedule(() => {
      void import("react-pdf");
      void import("pdfjs-dist");
    });
    return () => cancel(id as number);
  }, []);

  return (
    <div className="container mx-auto flex flex-col gap-12 px-4 py-6 md:px-8">
      <section
        className={cn(
          "relative z-10 grid items-center gap-10 py-6 lg:gap-12 lg:py-10",
          heroExpanded ? "lg:grid-cols-1" : "lg:grid-cols-2",
        )}
      >
        <header
          className={cn(
            "flex flex-col gap-6 text-balance text-center lg:text-left",
            heroExpanded && "hidden",
          )}
        >
          <h2 className="font-display text-5xl font-medium tracking-tight md:text-6xl">
            Work with any <em className="font-normal text-primary">PDF.</em>
          </h2>
          <p className="text-balance text-lg text-muted-foreground">
            Merge, sign, compress, and more. No account. No upload to us.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-start">
            <TrustSignal />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white/70 px-3 py-1.5 text-sm text-foreground/80">
              <Sparkle weight="fill" className="size-4 text-primary" />
              Free forever
            </span>
          </div>
        </header>

        <HeroDropzone onStagedChange={setHeroExpanded} />
      </section>

      {groups.map((group) => (
        <section key={group.category} className="relative z-0 flex flex-col gap-4">
          <h3 className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
            {CATEGORY_LABELS[group.category]}
          </h3>
          {/* subgrid: every card shares the same row template, so icons, titles,
              descriptions, and footers stay aligned across rows even when content
              length differs. */}
          <ul className="grid grid-rows-[auto_auto_1fr_auto] gap-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
            {group.actions.map((action) => (
              <li key={action.id} className="row-span-4 grid grid-rows-subgrid">
                <ActionCard action={action} />
              </li>
            ))}
          </ul>
        </section>
      ))}

      <WhySection />

      <HowItWorks />

      <CompareTable />

      <FAQ />

      {upcomingActions.length > 0 && (
        <section className="flex flex-col gap-4">
          <h3 className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
            Roadmap
          </h3>
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {upcomingActions.map((action) => (
              <ActionRowItem key={action.id} action={action} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
